import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Store, Building2, User } from 'lucide-react';
import { Button, Input } from '../components/ui';
import { sellerProfileHasShop } from '../utils/sellerShop';

const CreateShop: React.FC = () => {
  const navigate = useNavigate();
  const { token, setSellerFromCreateShop } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    type: 'individual' as 'individual' | 'enterprise',
    logo: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [isUpdatingExisting, setIsUpdatingExisting] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    const loadExistingProfile = async () => {
      setLoadingProfile(true);
      try {
        const res = await api.get('/seller/profile');
        const data = res.data?.data ?? res.data;

        if (sellerProfileHasShop(data)) {
          const sellerId = String(data.id ?? data._id);
          setSellerFromCreateShop({
            id: sellerId,
            name: data.name ?? '',
            type: data.type === 'company' || data.type === 'enterprise' ? 'company' : 'individual',
            logo: data.logo ?? '',
            official: Boolean(data.official),
          });
          navigate('/', { replace: true });
          return;
        }

        if (data?.name !== undefined || data?.id || data?._id) {
          setFormData({
            name: data.name ?? '',
            type: data.type === 'company' || data.type === 'enterprise' ? 'enterprise' : 'individual',
            logo: data.logo ?? '',
          });
          setIsUpdatingExisting(true);
        }
      } catch {
        setIsUpdatingExisting(false);
      } finally {
        setLoadingProfile(false);
      }
    };
    loadExistingProfile();
  }, [token, navigate, setSellerFromCreateShop]);

  const handleLogoUpload = async (file: File) => {
    setUploadingLogo(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await api.post('/upload-single-file', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.data?.fileUrl) {
        setFormData(prev => ({
          ...prev,
          logo: response.data.data.fileUrl
        }));
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      alert('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Shop name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isUpdatingExisting) {
        const res = await api.put('/seller/profile', {
          name: formData.name.trim(),
          type: formData.type === 'enterprise' ? 'company' : 'individual',
          logo: formData.logo || '',
          official: false,
        });
        const data = res.data?.data ?? res.data;
        const sellerId = data?.id ?? data?._id;
        if (sellerId) {
          setSellerFromCreateShop({
            id: String(sellerId),
            name: data.name ?? formData.name.trim(),
            type: data.type ?? formData.type,
            logo: data.logo ?? formData.logo ?? '',
            official: Boolean(data.official),
          });
        }
        navigate('/', { replace: true });
        return;
      }

      let currentUserId = localStorage.getItem('sellerUserId');
      if (!currentUserId) {
        try {
          const token = localStorage.getItem('sellerToken');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            currentUserId = payload.id || payload.userId || payload.sub;
          }
        } catch (decodeError) {
          console.error('Failed to decode token:', decodeError);
        }
      }

      if (!currentUserId) {
        throw new Error('Unable to get user ID. Please log in again.');
      }

      const res = await api.post('/auth/signup-seller', {
        user: currentUserId,
        name: formData.name.trim(),
        type: formData.type === 'enterprise' ? 'company' : 'individual',
        logo: formData.logo || '',
        official: false,
      });

      const data = res.data?.data ?? res.data;
      const sellerId = data?.id ?? data?._id;
      if (sellerId) {
        setSellerFromCreateShop({
          id: String(sellerId),
          name: data.name ?? formData.name.trim(),
          type: data.type ?? formData.type,
          logo: data.logo ?? formData.logo ?? '',
          official: Boolean(data.official),
        });
      }
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create shop. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-orange-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-custom-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Store className="text-custom-primary" size={48} />
          </div>
          <h1 className="text-4xl font-bold text-custom-primary mb-2">
            {isUpdatingExisting ? 'Complete Your Shop' : 'Create Your Shop'}
          </h1>
          <p className="text-gray-600">
            {isUpdatingExisting ? 'Add your shop name and logo to continue' : 'Set up your shop to start selling'}
          </p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <Input
              label="Shop Name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="My Awesome Shop"
              icon={Store}
              iconPosition="left"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Type
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, type: 'individual' })}
                  className={`p-4 border-2 rounded-lg transition-colors ${
                    formData.type === 'individual'
                      ? 'border-custom-primary bg-custom-secondary/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <User className="mx-auto mb-2 text-gray-600" size={24} />
                  <p className="font-medium text-gray-900">Individual</p>
                  <p className="text-xs text-gray-500 mt-1">Personal seller</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, type: 'enterprise' })}
                  className={`p-4 border-2 rounded-lg transition-colors ${
                    formData.type === 'enterprise'
                      ? 'border-custom-primary bg-custom-secondary/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Building2 className="mx-auto mb-2 text-gray-600" size={24} />
                  <p className="font-medium text-gray-900">Enterprise</p>
                  <p className="text-xs text-gray-500 mt-1">Business account</p>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shop Logo (Optional)
              </label>
              {formData.logo && (
                <img
                  src={formData.logo}
                  alt="Shop logo"
                  className="w-24 h-24 object-cover rounded-lg mb-4"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleLogoUpload(file);
                }}
                className="input-field"
                disabled={uploadingLogo}
              />
              {uploadingLogo && (
                <p className="text-sm text-gray-500 mt-2">Uploading...</p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              loading={loading || uploadingLogo}
            >
              {isUpdatingExisting ? 'Save & Continue' : 'Create Shop'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateShop;

